import { faroCoreApiClient } from "@api/faro-core-api-client";
import { useRefreshMemberGroup } from "@components/table/members/remove-xg-seat/use-refresh-member-group";
import { useCompanyId } from "@context-providers/company-id-provider";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { removeProjectsFromGroup, updateProjectRoleInMap } from "@store/company-member-groups/company-member-groups-slice";
import { removeMemberFromProject, updateMemberRoleInProjectSingle } from "@store/projects/projects-slice-thunk";
import { useAppDispatch } from "@store/store-helper";

/** What the member gives up in a project: the project as a whole, or only their editor role in it */

/** Returns how to change the role of the member in the projects of their groups */
export function useChangeMemberProjects({
  member
}) {
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const {
    handleErrorWithToast
  } = useErrorContext();
  const refreshGroup = useRefreshMemberGroup(member.identity);
  async function changeProject({
    project,
    group
  }, change) {
    if (change === "remove") {
      await dispatch(removeMemberFromProject({
        companyId,
        projectId: project.id,
        member
      })).unwrap();
      await refreshGroup(group.id, () => dispatch(removeProjectsFromGroup({
        groupId: group.id,
        projectIds: [project.id]
      })));
      return;
    }
    await dispatch(updateMemberRoleInProjectSingle({
      companyId,
      projectId: project.id,
      userIdentity: member.identity,
      payload: {
        role: "RESOURCE_VIEWER"
      }
    })).unwrap();
    await refreshGroup(group.id, () => dispatch(updateProjectRoleInMap({
      projectId: project.id,
      newRole: "RESOURCE_VIEWER"
    })));
  }
  async function settleFormerManager(memberProject, shouldKeepAsViewer) {
    const {
      project: project_0,
      group: group_0
    } = memberProject;
    const refreshedGroup = await refreshGroup(group_0.id, () => undefined);
    const role = refreshedGroup?.projects.find(({
      id
    }) => id === project_0.id)?.role;
    try {
      if (!role) {
        if (shouldKeepAsViewer && refreshedGroup !== undefined) {
          await addAsViewer(project_0);
        }
        return;
      }
      if (!shouldKeepAsViewer) {
        await changeProject(memberProject, "remove");
      } else if (role !== "RESOURCE_VIEWER") {
        await changeProject(memberProject, "downgrade");
      }
    } catch {
      // The error toast of the request tells the user
    }
  }

  /**
   * Adds the member back to a project as a viewer, after handing it over left them out of it
   *
   * @throws when that failed, telling the user why
   */
  async function addAsViewer(project_1) {
    try {
      await faroCoreApiClient.projects.addMembersToProject({
        companyId,
        projectId: project_1.id,
        payload: {
          assignments: [{
            identities: [member.identity],
            role: "RESOURCE_VIEWER"
          }]
        }
      });
    } catch (error) {
      handleErrorWithToast({
        id: `keepFormerManagerAsViewer-${Date.now().toString()}`,
        title: "Cannot keep the member in the project as a viewer",
        error
      });
      throw error;
    }
  }
  return {
    changeProject,
    settleFormerManager
  };
}